import React, { useContext, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  RealtimeDBIdentityVerificationStatus,
  UserStatus,
  VerificationStatus,
} from '@playback-rewards/shared-libraries'

//components
import MessageLayout from 'components/layout/Message'
import FullPageLoader from 'components/pageLoader'
import ContactSupport from './ContactSupport'
//constants
import { ANALYTICS_EVENT, ERROR_TEXT, LOCAL_STORAGE } from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
//hooks
import useLocalStorage from 'hooks/useLocalStorage'
import useRealtimeIdentityVerificationStatus from 'hooks/useRealtimeIdentityVerificationStatus'
import useOrderGiftCardMutation from 'hooks/useOrderGiftCardMutation'
import useOrderGiftCardByCampaignMutation from '../../hooks/useOrderGiftCardByCampaignMutation'
import { verifyVerisoulSession } from 'services/verisoulService'
import { sendGA4Event } from 'utils/utils'

const getFailureCopy = (
  { status, failureReason }: { status: string; failureReason?: string },
  outOfAttempts: boolean
) => {
  const noAttemptsLeft = outOfAttempts && <p>You have no verification attempts left.</p>

  switch (status) {
    case VerificationStatus.UNVERIFIED:
    case VerificationStatus.STRIPE_REJECTED:
      return {
        title: 'We were unable to verify your identity using the information provided',
        text: outOfAttempts ? (
          <>
            <p>Unfortunately we were unable to verify your identity.</p>
            {noAttemptsLeft}
            <p>
              Please <ContactSupport /> for further assistance.
            </p>
          </>
        ) : (
          <>
            <p>Verification failure reason: {failureReason}</p>
            <p>
              Please double-check the information you provided and try again. If you continue to
              experience issues, please <ContactSupport /> for assistance.
            </p>
          </>
        ),
        showPlaybackLogo: true,
      }
    case VerificationStatus.PLAYBACK_DUPLICATE_REJECTED:
      return {
        title: 'This ID is already connected to another Playback account.',
        text: (
          <>
            <p>
              Unfortunately, it appears that another account has used this same ID previously.
              Playback only allows one account per person. To continue using Playback please return
              to using the original account connected to this ID.
            </p>
            <p>
              If you believe this identity verification is incorrect or you have any questions,
              please <ContactSupport />.
            </p>
          </>
        ),
        showPlaybackLogo: false,
      }
    case VerificationStatus.COUNTRY_MISMATCH:
      return {
        title: 'The country of the ID used does not match the country of your Playback account.',
        text: (
          <>
            <p>
              It was found that your ID verification was in violations of our Terms of Service. The
              country associated with your account did not match the country of the ID that you used
              to verify your identity. As a result, we are unable to verify your account.
            </p>
            {noAttemptsLeft}
            <p>
              If you believe this identity verification is incorrect or you have any questions,
              please <ContactSupport />.
            </p>
          </>
        ),
        showPlaybackLogo: false,
      }
    case VerificationStatus.COUNTRY_INELIGIBLE:
      return {
        title: 'The country of the ID used is not a supported country for Playback.',
        text: (
          <>
            <p>
              It was found that your ID is from a country that Playback does not support so we will
              be unable to verify your account.
            </p>
            {noAttemptsLeft}
            <p>
              If you believe this identity verification is incorrect or you have any questions,
              please <ContactSupport />.
            </p>
          </>
        ),
        showPlaybackLogo: false,
      }
    default:
      return {
        title: 'We were unable to verify your identity',
        text: (
          <>
            {noAttemptsLeft}
            <p>
              If you have any questions, please <ContactSupport />.
            </p>
          </>
        ),
        showPlaybackLogo: true,
      }
  }
}

const VerificationResult = () => {
  const { user } = useContext(AuthContext)
  const { isLoading } = useContext(LoaderContext)

  const { getItem } = useLocalStorage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const verifySessionRequestedRef = useRef(false)
  const identityVerificationFinishEventSentRef = useRef(false)

  const { verificationResult, outOfAttempts } = useRealtimeIdentityVerificationStatus(user?.id)
  const { createOrderMutate } = useOrderGiftCardMutation()
  const { orderGiftCardMutate } = useOrderGiftCardByCampaignMutation()

  useEffect(() => {
    const provider = searchParams.get('provider')
    const sessionId = searchParams.get('session_id')
    const verificationStatus = user?.identityVerification?.status

    if (
      verifySessionRequestedRef.current ||
      provider !== 'verisoul' ||
      !sessionId ||
      !user?.id ||
      verificationStatus === VerificationStatus.VERIFIED ||
      verificationStatus === VerificationStatus.VERIFYING
    ) {
      return
    }

    verifySessionRequestedRef.current = true
    verifyVerisoulSession({ userId: user.id, sessionId }).catch(() => {
      toast.error('Unable to verify your identity at this time. Please try again.')
    })
  }, [searchParams, user?.id, user?.identityVerification?.status])

  useEffect(() => {
    const provider = searchParams.get('provider')
    const sessionId = searchParams.get('session_id')

    if (
      identityVerificationFinishEventSentRef.current ||
      provider !== 'verisoul' ||
      !sessionId ||
      verificationResult !== RealtimeDBIdentityVerificationStatus.FINISHED
    ) {
      return
    }

    identityVerificationFinishEventSentRef.current = true
    sendGA4Event(ANALYTICS_EVENT.IDENTITY_VERIFICATION_FINISH, {
      session_id: sessionId,
    })
  }, [searchParams, verificationResult])

  const verificationResultContent = useMemo(() => {
    if (!user) {
      return null
    }
    switch (user.identityVerification.status) {
      case VerificationStatus.VERIFIED: {
        return {
          title: 'Congrats! Your identify has been verified!',
          buttonText: 'Finish Gift Card Redemption',
          onButtonClick: () => {
            const giftCardId = getItem(LOCAL_STORAGE.GIFT_CARD_ID)
            const campaignId = getItem(LOCAL_STORAGE.TREMENDOUS_CAMPAIGN_ID)
            const denomination = getItem(LOCAL_STORAGE.GIFT_CARD_DENOMINATION)

            if (giftCardId && denomination && user.userStatus === UserStatus.ACTIVE) {
              return createOrderMutate({ giftCardId, denomination })
            }
            if (campaignId && denomination && user.userStatus === UserStatus.ACTIVE) {
              return orderGiftCardMutate({ campaignId, denomination })
            }
            if (user.userStatus === UserStatus.FROZEN) {
              toast.error(ERROR_TEXT.account_frozen_gift_card, { duration: 10000 })
            } else {
              toast.error(ERROR_TEXT.choose_gift_card)
            }
            navigate('/rewards')
          },
          text: (
            <>
              <p>
                Thank you for completing our identity verification process. Your account has now
                been fully verified and you can now use all the features of our platform without any
                restrictions.
              </p>
              <p>
                If you have any questions or concerns, please do not hesitate to contact our support
                team. We are always here to help you. Thank you again for your cooperation and for
                being a valued member of our platform.
              </p>
            </>
          ),
          showPlaybackLogo: true,
          secondaryButton: undefined,
        }
      }
      case VerificationStatus.VERIFYING:
        return null
    }

    const canRetry =
      !outOfAttempts &&
      user.identityVerification.status !== VerificationStatus.PLAYBACK_DUPLICATE_REJECTED

    return {
      ...getFailureCopy(user.identityVerification, outOfAttempts),
      ...(canRetry
        ? {
            buttonText: 'Retry',
            onButtonClick: () => navigate('/verification'),
            secondaryButton: { text: 'Done', handleClick: () => navigate('/games') },
          }
        : { buttonText: 'Done', onButtonClick: () => navigate('/games') }),
    }
  }, [user, outOfAttempts])

  const shouldShowProcessing =
    verificationResult !== RealtimeDBIdentityVerificationStatus.FINISHED ||
    !verificationResultContent

  if (isLoading) {
    return <FullPageLoader />
  }

  if (shouldShowProcessing) {
    return (
      <MessageLayout
        isLoading={isLoading}
        title="Currently processing your identity data"
        handleBackArrowClick={() => navigate('/rewards')}
      >
        <p>When Playback receives your identity verification results we will update this page</p>
      </MessageLayout>
    )
  }

  return (
    <MessageLayout
      title={verificationResultContent.title}
      button={{
        text: verificationResultContent.buttonText,
        handleClick: verificationResultContent.onButtonClick,
      }}
      secondaryButton={verificationResultContent.secondaryButton}
      handleBackArrowClick={() => navigate('/games')}
      showPlaybackLogo={verificationResultContent.showPlaybackLogo}
    >
      {verificationResultContent?.text}
    </MessageLayout>
  )
}

export default VerificationResult
